/**
 * Weaving, the stash and Grand Magicks (docs/spells-depth.md).
 *
 * The weave key joins the LAST TWO pieces of the queue into a deeper one
 * when they make a recipe. Recipes are unordered (Lava + Ice and Ice + Lava
 * are both Obsidian). A woven piece's tier is one more than its deeper
 * parent's; deeper weaves take longer. Woven pieces are stable: no key
 * cancels or degrades them (elements.ts OPPOSITES has nothing for them, and
 * queue.ts only ever looks for base and fused elements).
 *
 * The stash holds woven or fused pieces (never base elements) in two slots.
 * A pulled piece goes in whole: it is appended without fusing, cancelling or
 * degrading, which is how Lava meets Ice.
 *
 * A Grand Magick is cast with the Magick key when the queue and the stash
 * together hold its pieces and the queue holds nothing else.
 */

import type { Element, WovenElement } from './elements.js';
import { MAX_QUEUE, isBase, isWoven } from './elements.js';

export type Tier = 1 | 2 | 3 | 4 | 5;

export interface WeaveRecipe { a: Element; b: Element; out: WovenElement; tier: Tier }

/**
 * Slice 1 of the tree: the time line, the forge line's glass and dragonfire, and the sky line's cloud and ash.
 * Slice 2: the sea (Tide, Whirlpool, Deluge), the fire that spreads (Wildfire, Inferno, Sun), the long cold
 * (Glacier, Winter) and the soul line (Pulse, Void, Soul, Death).
 */
export const RECIPES: readonly WeaveRecipe[] = [
  { a: 'fire', b: 'lightning', out: 'plasma', tier: 2 },
  { a: 'lightning', b: 'arcane', out: 'flux', tier: 2 },
  { a: 'cold', b: 'shield', out: 'stillness', tier: 2 },
  { a: 'lava', b: 'ice', out: 'obsidian', tier: 3 },
  { a: 'steam', b: 'arcane', out: 'ether', tier: 3 },
  { a: 'steam', b: 'steam', out: 'cloud', tier: 3 },
  { a: 'lava', b: 'steam', out: 'ash', tier: 3 },
  { a: 'ice', b: 'cold', out: 'rime', tier: 3 },
  { a: 'flux', b: 'stillness', out: 'moment', tier: 3 },
  { a: 'plasma', b: 'lava', out: 'dragonfire', tier: 3 },
  { a: 'obsidian', b: 'lightning', out: 'stormglass', tier: 4 },
  { a: 'cloud', b: 'lightning', out: 'thunderhead', tier: 4 },
  { a: 'moment', b: 'moment', out: 'time', tier: 4 },
  { a: 'water', b: 'water', out: 'tide', tier: 2 },
  { a: 'life', b: 'lightning', out: 'pulse', tier: 2 },
  { a: 'arcane', b: 'shield', out: 'void', tier: 2 },
  { a: 'fire', b: 'arcane', out: 'wildfire', tier: 2 },
  { a: 'tide', b: 'flux', out: 'whirlpool', tier: 3 },
  { a: 'whirlpool', b: 'cloud', out: 'deluge', tier: 4 },
  { a: 'dragonfire', b: 'wildfire', out: 'inferno', tier: 4 },
  { a: 'rime', b: 'earth', out: 'glacier', tier: 4 },
  { a: 'ether', b: 'life', out: 'soul', tier: 4 },
  { a: 'inferno', b: 'ether', out: 'sun', tier: 5 },
  { a: 'glacier', b: 'thunderhead', out: 'winter', tier: 5 },
  { a: 'soul', b: 'void', out: 'death', tier: 5 },
];

const BY_OUT: ReadonlyMap<Element, WeaveRecipe> = new Map(RECIPES.map((r) => [r.out, r]));

/** Book text per woven piece: a two-letter chip label, what it means, and what its Force cast does. */
export const WOVEN_INFO: Readonly<Record<WovenElement, { short: string; means: string; force: string }>> = {
  plasma: { short: 'Pl', means: 'Fire hot enough to carry a current.', force: 'An arc that burns as it chains; burning targets it touches pop.' },
  flux: { short: 'Fx', means: 'Magic in motion.', force: 'A beam that drags what it holds and steals its speed for you.' },
  stillness: { short: 'St', means: 'Nothing moves.', force: 'A stasis zone: everything inside slows, projectiles stop and drop.' },
  obsidian: { short: 'Ob', means: 'Volcanic glass.', force: 'A glass bolt that shatters into piercing shards. Frozen targets shatter.' },
  ether: { short: 'Et', means: 'The sky where magic floats.', force: 'A beam that passes through walls, barriers and shields.' },
  cloud: { short: 'Cl', means: 'A raincloud on a leash.', force: 'A cloud that soaks everyone under it and puts fires out.' },
  ash: { short: 'As', means: 'A choking, burning cloud.', force: 'Hot ash: burns, and blinds enemies so they lose their target.' },
  rime: { short: 'Ri', means: 'Frost that freezes the dry.', force: 'A cold spray that freezes even dry targets.' },
  moment: { short: 'Mo', means: 'One frozen instant.', force: 'A bubble where time stops for 2 s.' },
  dragonfire: { short: 'Df', means: "Dragon's breath.", force: 'A fire beam that leaves burning ground along its length.' },
  stormglass: { short: 'SG', means: 'Glass holding a storm.', force: 'A glass bolt whose shards stay joined by lightning.' },
  thunderhead: { short: 'Th', means: 'A thunderhead.', force: 'A storm cloud that strikes the enemy under it, wet ones first.' },
  time: { short: 'Ti', means: 'Many moments.', force: 'An hourglass: everything near it runs at 30%.' },
  tide: { short: 'Td', means: 'The weight of the sea.', force: 'A rolling wave that carries everyone it meets along with it.' },
  pulse: { short: 'Pu', means: 'A heartbeat.', force: 'An arc through four bodies that heals every one of them, enemies too.' },
  void: { short: 'Vo', means: 'Absence; magic unmade.', force: 'A slow dark orb that eats missiles, mines, storms and barriers.' },
  wildfire: { short: 'Wf', means: 'Fire with a will.', force: 'A fire spray whose burning jumps to the next body every second.' },
  whirlpool: { short: 'Wp', means: 'Water going round.', force: 'A whirlpool that drags everyone near it into the middle and soaks them.' },
  deluge: { short: 'Dl', means: 'The sky falls in.', force: 'A flood: everyone in it is wet, and lightning runs through the water.' },
  inferno: { short: 'In', means: "Fire that won't stop.", force: 'Six burn stacks that spread, and burning ground behind them.' },
  glacier: { short: 'Gl', means: 'A frozen mountain.', force: 'A huge ice boulder that rolls on, crushing and freezing all in its path.' },
  soul: { short: 'So', means: "What's left when the body's gone.", force: 'A beam that heals friends and drains enemies; a kill sends you its wisp.' },
  sun: { short: 'Su', means: 'Fire in the sky.', force: 'A sunburst where you aim: 1500 fire, four burn stacks, and it blinds.' },
  winter: { short: 'Wi', means: 'The long cold.', force: 'A blizzard where you aim: everyone inside is chilled each second, frozen if wet.' },
  death: { short: 'De', means: 'The end of things.', force: 'Marks a body; five seconds later it loses 30% of its life.' },
};

/** Weave timings and limits. `seconds` by the result's tier. */
export const WEAVE = {
  seconds: { 2: 0.4, 3: 0.7, 4: 1.1, 5: 1.6 } as Readonly<Record<2 | 3 | 4 | 5, number>>,
  /** Walking speed while weaving. */
  moveMul: 0.5,
  /** One hit at least this big breaks a weave (after PvP scaling in PvP). */
  breakDamage: 250,
  breakDamagePvp: 120,
  /** Cast lock after a weave that won't hold. */
  failLock: 0.3,
} as const;

export const STASH_SLOTS = 2;

/** Tier 1 for the eight elements, 2 for the key fusions, the recipe's tier for woven pieces. */
export function tierOf(el: Element): Tier {
  if (isBase(el)) return 1;
  return BY_OUT.get(el)?.tier ?? 2;
}

/** The recipe that makes `out`, for the book. */
export function recipeOf(out: Element): WeaveRecipe | null {
  return BY_OUT.get(out) ?? null;
}

/** The recipe two pieces make, in either order, or null. */
export function recipeFor(a: Element, b: Element): WeaveRecipe | null {
  for (const r of RECIPES) if ((r.a === a && r.b === b) || (r.a === b && r.b === a)) return r;
  return null;
}

/** The recipe the last two queued pieces make, or null (fewer than two, or no recipe). */
export function weaveTarget(queue: readonly Element[]): WeaveRecipe | null {
  const n = queue.length;
  if (n < 2) return null;
  const a = queue[n - 2];
  const b = queue[n - 1];
  return a !== undefined && b !== undefined ? recipeFor(a, b) : null;
}

/** Seconds a weave into `recipe` takes. */
export function weaveSeconds(recipe: WeaveRecipe): number {
  return WEAVE.seconds[recipe.tier === 1 ? 2 : recipe.tier];
}

/** The queue after a finished weave: the last two pieces become the result. Never mutates. */
export function applyWeave(queue: readonly Element[], recipe: WeaveRecipe): Element[] {
  return [...queue.slice(0, -2), recipe.out];
}

// ---------------------------------------------------------------- the stash

/** A stash slot: a piece, or null when empty. */
export type StashSlot = Element | null;

/** Only fused and woven pieces can be stashed. */
export function canStash(el: Element): boolean {
  return !isBase(el);
}

/** Index of the newest stashable piece in the queue, or -1. */
export function stashIndex(queue: readonly Element[]): number {
  for (let i = queue.length - 1; i >= 0; i--) {
    const el = queue[i];
    if (el !== undefined && canStash(el)) return i;
  }
  return -1;
}

export type StashOutcome = 'stashed' | 'pulled' | 'nothing' | 'full';

export interface StashResult { queue: Element[]; stash: StashSlot[]; outcome: StashOutcome }

/**
 * Tap a stash slot: an empty slot takes the newest stashable piece out of the
 * queue; a full one puts its piece at the end of the queue, whole. `nothing`:
 * an empty slot and nothing stashable; `full`: a full slot and a full queue.
 * Never mutates.
 */
export function tapStash(queue: readonly Element[], stash: readonly StashSlot[], slot: number): StashResult {
  const s = [...stash];
  while (s.length < STASH_SLOTS) s.push(null);
  const held = s[slot] ?? null;
  if (held === null) {
    const i = stashIndex(queue);
    if (i < 0) return { queue: [...queue], stash: s, outcome: 'nothing' };
    const el = queue[i] as Element;
    s[slot] = el;
    return { queue: [...queue.slice(0, i), ...queue.slice(i + 1)], stash: s, outcome: 'stashed' };
  }
  if (queue.length >= MAX_QUEUE) return { queue: [...queue], stash: s, outcome: 'full' };
  s[slot] = null;
  return { queue: [...queue, held], stash: s, outcome: 'pulled' };
}

// ---------------------------------------------------------------- Grand Magicks

export type GrandId = 'timestop' | 'quickening' | 'rewind' | 'greatflood' | 'supernova' | 'eternalwinter' | 'doom';

export interface GrandDef {
  pieces: Element[]; name: string; desc: string;
  /** Seconds of incantation before it happens. */
  incant: number;
  /** Per-player cooldown in seconds, co-op and PvP. */
  cooldown: number; pvpCooldown: number;
  /** Not castable in PvP (Rewind). */
  pvpOff?: true;
}

export const GRAND_IDS: readonly GrandId[] = ['timestop', 'quickening', 'rewind', 'greatflood', 'supernova', 'eternalwinter', 'doom'];

export const GRAND_MAGICKS: Readonly<Record<GrandId, GrandDef>> = {
  timestop: {
    pieces: ['time', 'stillness'],
    name: 'Time Stop',
    desc: 'Everyone else, friends included, runs at a twentieth of their speed for 6 s. You don\'t.',
    incant: 1.5,
    cooldown: 90,
    pvpCooldown: 180,
  },
  quickening: {
    pieces: ['time', 'flux'],
    name: 'Quickening',
    desc: 'Your whole party moves 1.6x as fast, and weaves and casts faster, for 10 s (PvP: just you).',
    incant: 1,
    cooldown: 60,
    pvpCooldown: 120,
  },
  rewind: {
    pieces: ['time', 'pulse'],
    name: 'Rewind',
    desc: 'Your party snaps back to where it stood 5 s ago, with the life it had then; anyone who died since stands up.',
    incant: 1.5,
    cooldown: 120,
    pvpCooldown: 0,
    pvpOff: true,
  },
  greatflood: {
    pieces: ['deluge', 'tide'],
    name: 'The Great Flood',
    desc: 'The whole room floods for 10 s: every enemy is soaked, your side stays dry, and lightning on one soaked body runs to all of them.',
    incant: 1.5,
    cooldown: 90,
    pvpCooldown: 150,
  },
  supernova: {
    pieces: ['sun', 'plasma'],
    name: 'Supernova',
    desc: 'A star appears where you aim, swells for 2 s and bursts: 5000 fire and lightning within 7 u. Throw it and run.',
    incant: 2,
    cooldown: 120,
    pvpCooldown: 180,
  },
  eternalwinter: {
    pieces: ['winter', 'stillness'],
    name: 'Eternal Winter',
    desc: 'Every enemy freezes solid, and for 12 s the cold keeps coming back: chilled every second, frozen if wet. Your side is spared.',
    incant: 2,
    cooldown: 120,
    pvpCooldown: 180,
  },
  doom: {
    pieces: ['death', 'time'],
    name: 'Doom',
    desc: 'Every enemy near you is marked; 6 s later each loses half its life (bosses a tenth). PvP: one wizard, 40%.',
    incant: 2,
    cooldown: 120,
    pvpCooldown: 180,
  },
};

/** Time Stop's numbers (docs/spells-depth.md 4.1; slice 2 made the Grand one stronger: 6 s at 5%). */
export const TIME_STOP = {
  seconds: 6,
  pvpSeconds: 2.5,
  /** Time scale of everyone else. */
  scale: 0.05,
  pvpScale: 0.25,
  bossScale: 0.25,
  /** PvP: anyone holding a Moment or Time runs at this instead. */
  touchedScale: 0.5,
  /** PvP: the caster's damage to stopped players is multiplied by this. */
  pvpDamageMul: 0.5,
  /** PvP: no Grand Magick in the first seconds of a match. */
  pvpGrace: 30,
} as const;

/**
 * The Lesser Time Stop (an ordinary Magick: Moment, then Time Warp's keys R E;
 * magicks.ts 'stoptime'). Shorter and easier, and it spares your side: only
 * enemies (PvP: the other team) slow. Bosses feel it at a quarter. Its own
 * cooldown in co-op; in PvP it sets the one Magick cooldown.
 */
export const LESSER_STOP = {
  seconds: 3,
  pvpSeconds: 1.5,
  scale: 0.15,
  pvpScale: 0.4,
  /** PvP: a wizard holding a Moment or Time runs at this instead. */
  touchedScale: 0.7,
  cooldown: 30,
  pvpCooldown: 60,
} as const;

/** Quickening: the party's time runs this much faster (moving, weaving, cast locks). */
export const QUICKENING = { seconds: 10, scale: 1.6 } as const;

/** Rewind: how far back it reaches, and how often the party's places are written down. */
export const REWIND = { seconds: 5, sampleEvery: 0.25 } as const;

/** The Great Flood: how long, and how often it soaks everyone. Lightning runs through it at STORY.conductShare. */
export const GREAT_FLOOD = { seconds: 10, pulse: 1 } as const;

/**
 * Supernova: the star's place and swell, its burst, and its PvP number (per wizard, final). The star sits 3 u
 * beyond its burst's reach, so a caster who stands still is just clear; one who walks toward it is not.
 */
export const SUPERNOVA = { dist: 10, swell: 2, radius: 7, fire: 2500, lightning: 2500, blindRadius: 12, pvpDamage: 1200 } as const;

/** Eternal Winter: how long, and how often the cold comes back (often enough that a thawing body is caught again at once). */
export const ETERNAL_WINTER = { seconds: 12, pulse: 0.5 } as const;

/**
 * Death marks (the Death concept and Doom): seconds until the mark falls, the
 * share of max life it takes (bosses a smaller share), and how far each reaches.
 */
export const DEATH_MARK = { delay: 5, frac: 0.3, bossFrac: 0.05, range: 12, radius: 4 } as const;
export const DOOM = { delay: 6, frac: 0.5, bossFrac: 0.1, pvpFrac: 0.4, reach: 16 } as const;

export interface GrandMatch { id: GrandId; slots: number[] }

/**
 * The Grand Magick the queue and stash make together, or null. The queue must
 * hold only the Magick's pieces; the rest must sit in the stash. `slots` are
 * the stash slots it uses. A queue without a woven piece never matches, so
 * ordinary Magicks keep their keys. When two could match (Time in the queue,
 * Stillness and Flux both stashed), the one that needs fewer stash slots wins,
 * then the one listed first: pull the piece you mean into the queue.
 */
export function matchGrand(queue: readonly Element[], stash: readonly StashSlot[]): GrandMatch | null {
  let best: GrandMatch | null = null;
  for (const m of grandMatches(queue, stash)) if (!best || m.slots.length < best.slots.length) best = m;
  return best;
}

function grandMatches(queue: readonly Element[], stash: readonly StashSlot[]): GrandMatch[] {
  const out: GrandMatch[] = [];
  for (const id of GRAND_IDS) {
    const need = [...GRAND_MAGICKS[id].pieces];
    let ok = true;
    for (const el of queue) {
      const i = need.indexOf(el);
      if (i < 0) { ok = false; break; }
      need.splice(i, 1);
    }
    if (!ok) continue;
    const slots: number[] = [];
    for (let s = 0; s < stash.length && need.length > 0; s++) {
      const el = stash[s];
      if (el === null || el === undefined) continue;
      const i = need.indexOf(el);
      if (i >= 0) { need.splice(i, 1); slots.push(s); }
    }
    if (need.length > 0) continue;
    if (!queue.some(isWoven) && slots.length === 0) continue;
    out.push({ id, slots });
  }
  return out;
}

/** True for the pieces that make a wizard "time-touched" against Time Stop in PvP. */
export function isTimePiece(el: Element | null | undefined): boolean {
  return el === 'moment' || el === 'time';
}
